import { authVersion, invalidateAdminSession } from '@/services/authLifecycle';
import { queryClient } from '@/services/queryClient';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AdminUser } from '@/types';
import { api, endpoints, clearCsrfToken, fetchCsrfToken, setCsrfToken } from '@/services/api';
import toast from 'react-hot-toast';

interface AuthState {
  user: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  clearError: () => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: async (email: string, password: string) => {
        invalidateAdminSession();
        const version = authVersion();
        try {
          set({ isLoading: true, error: null });

          // Demo login credentials
          /* if (email === 'admin@incoinx.com' && password === 'admin123456') {
            // Simulate API delay
            await new Promise(resolve => setTimeout(resolve, 1000));

            const demoUser: AdminUser = {
              id: 'admin_001',
              name: 'Admin User',
              email: 'admin@incoinx.com',
              role: 'admin' as any,
              avatar: '',
              createdAt: new Date().toISOString(),
              lastLogin: new Date().toISOString(),
              permissions: ['all'],
              status: 'active'
            };

            const accessToken = 'demo_admin_token_' + Date.now();
            
            // Set auth token
            setAuthToken(accessToken);

            // Update store
            set({
              user: demoUser,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });

            toast.success('Login successful! Welcome to InCoinX Admin Panel.');
            return;
          } */

          // For non-demo credentials, try the actual API
          try {
            if (import.meta.env.DEV) console.log('🔐 Attempting login with:', email);

            const response = await api.post<{
              status: string;
              message: string;
              data: {
                user: AdminUser;
                accessToken: string;
                refreshToken: string;
                csrfToken?: string;
              };
            }>(endpoints.auth.login, {
              identifier: email,
              password
            });

            if (version !== authVersion()) return;
            if (response.status === 'success' && response.data) {
              const { user, csrfToken } = response.data as { user: AdminUser; csrfToken?: string };

              if (import.meta.env.DEV) console.log('👤 User role:', user.role);

              // Check if user is admin or moderator
              if (user.role !== 'admin') {
                throw new Error('Insufficient permissions. Admin access required.');
              }

              if (csrfToken) {
                setCsrfToken(csrfToken);
              } else {
                await fetchCsrfToken();
              }

              // Tokens are now in httpOnly cookies - no manual storage needed
              // Update store
              set({
                user,
                isAuthenticated: true,
                isLoading: false,
                error: null,
              });

              toast.success('Login successful! Welcome to InCoinX Admin Panel.');
            } else {
              throw new Error(response.message || 'Login failed');
            }
          } catch (apiError: any) {
            // If API fails, show more detailed error
            const errorMessage = apiError.response?.data?.message || apiError.message || 'Invalid email or password';
            throw new Error(errorMessage);
          }
        } catch (error: any) {
          if (version !== authVersion()) return;
          const errorMessage = error.message || 'Login failed';

          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });

          toast.error(errorMessage);
          throw error;
        }
      },

      logout: () => {
        invalidateAdminSession();
        queryClient.cancelQueries();
        queryClient.clear();
        try {
          clearCsrfToken();
          // Call backend logout to clear cookies
          api.post(endpoints.auth.logout).catch(() => {
            // Ignore errors on logout
          });

          // Reset store
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });

          toast.success('Logged out successfully');
        } catch (error) {
          console.error('Logout error:', error);
        }
      },

      refreshUser: async () => {
        const version = authVersion();
        try {
          set({ isLoading: true });

          // Try to get current user (cookie will be sent automatically)
          const response = await api.get<{
            status: string;
            data: {
              user: AdminUser;
            };
          }>(endpoints.auth.me);

          if (version !== authVersion()) return;
          if (response.status === 'success' && response.data) {
            const { user } = response.data;

            // Check if user still has admin permissions
            if (user.role !== 'admin') {
              get().logout();
              toast.error('Your admin permissions have been revoked');
              return;
            }

            queryClient.clear();
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });

            await fetchCsrfToken();
          } else {
            throw new Error('Failed to refresh user data');
          }
        } catch (error: any) {
          if (version !== authVersion()) return;
          console.error('Refresh user error:', error);

          // If token is invalid, logout
          if (error.response?.status === 401) {
            get().logout();
          } else {
            set({ isLoading: false });
          }
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'admin-auth-store',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Auto-refresh user on app start if authenticated
if (useAuthStore.getState().isAuthenticated) {
  useAuthStore.getState().refreshUser();
}
window.addEventListener('admin:session-expired', () => {
  invalidateAdminSession();
  clearCsrfToken(); queryClient.cancelQueries(); queryClient.clear();
  useAuthStore.setState({ user: null, isAuthenticated: false, isLoading: false, error: 'Session expired. Please sign in again.' });
});
